import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Activity,
  AlertTriangle,
  WifiOff,
  Settings,
} from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [attention, setAttention] = useState(null);
  const [period, setPeriod] = useState("30d");

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [overviewRes, attentionRes] = await Promise.all([
        api.get("/admin/dashboard/overview", { params: { period } }),
        api.get("/admin/dashboard/attention"),
      ]);
      setData(overviewRes.data);
      setAttention(attentionRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados do dashboard");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400">Carregando dados...</p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      name: "Total Usuários",
      value: data?.users?.total || 0,
      change: `+${data?.users?.new_in_period || 0} novos`,
      trend: data?.users?.growth_rate > 0 ? "up" : "down",
      icon: Users,
      color: "blue",
    },
    {
      name: "Tenants Ativos",
      value: data?.tenants?.active || 0,
      change: `${data?.tenants?.total || 0} total`,
      icon: Building2,
      color: "emerald",
    },
    {
      name: "MRR",
      value: `R$ ${(data?.revenue?.mrr || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
      })}`,
      change: `ARR: R$ ${(data?.revenue?.arr_projected || 0).toLocaleString("pt-BR")}`,
      icon: DollarSign,
      color: "emerald",
    },
    {
      name: "Taxa de Recuperação",
      value: `${data?.transactions?.recovery_rate || 0}%`,
      change: `${data?.transactions?.recovered || 0} recuperados`,
      icon: TrendingUp,
      color: "emerald",
    },
    {
      name: "PIX Processados",
      value: data?.transactions?.total || 0,
      change: `R$ ${(data?.transactions?.total_pix_amount || 0).toLocaleString("pt-BR")}`,
      icon: Activity,
      color: "cyan",
    },
    {
      name: "Mensagens Enviadas",
      value: data?.messages?.total || 0,
      change: `${data?.messages?.success_rate || 0}% entregues`,
      icon: MessageSquare,
      color: "purple",
    },
  ];

  const planDistribution = Object.entries(data?.plans?.distribution || {});

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard Overview</h1>
          <p className="text-zinc-400 mt-1">Visão geral do seu SaaS</p>
        </div>
        <div className="flex gap-2">
          {["7d", "30d", "90d", "all"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                period === p
                  ? "bg-emerald-500 text-white"
                  : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
              }`}
            >
              {p === "all" ? "Tudo" : p.replace("d", " dias")}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-zinc-400 text-sm font-medium">{stat.name}</p>
                  <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                  <p className="text-sm text-zinc-500 mt-1">{stat.change}</p>
                </div>
                <div
                  className={`w-12 h-12 rounded-lg bg-${stat.color}-500/10 flex items-center justify-center`}
                >
                  <Icon className={`w-6 h-6 text-${stat.color}-500`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Plans Distribution */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          Distribuição por Plano
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {planDistribution.length > 0 ? (
            planDistribution.map(([planId, count]) => (
              <div
                key={planId}
                className="bg-zinc-800 rounded-lg p-4 border border-zinc-700"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm capitalize">
                      {planId === "none" ? "Sem plano" : planId}
                    </p>
                    <p className="text-2xl font-bold text-white mt-1">{count}</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-zinc-500 col-span-full text-center py-8">
              Nenhum dado de plano disponível
            </p>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transactions Stats */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Transações ({period})
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Total de PIX</span>
              <span className="text-white font-semibold">
                {data?.transactions?.in_period || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Recuperados</span>
              <span className="text-emerald-500 font-semibold flex items-center gap-2">
                <CheckCircle2 size={16} />
                {data?.transactions?.recovered || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Valor Recuperado</span>
              <span className="text-emerald-500 font-semibold">
                R$ {(data?.transactions?.recovered_amount || 0).toLocaleString("pt-BR")}
              </span>
            </div>
          </div>
        </div>

        {/* Messages Stats */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Mensagens ({period})
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Enviadas</span>
              <span className="text-white font-semibold">
                {data?.messages?.in_period || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Com falha</span>
              <span className="text-red-500 font-semibold flex items-center gap-2">
                <XCircle size={16} />
                {data?.messages?.failed || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Taxa de sucesso</span>
              <span className="text-emerald-500 font-semibold">
                {data?.messages?.success_rate || 0}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Attention Lists */}
      {attention && (
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-white">🚨 Itens que Precisam de Atenção</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* WhatsApp Disconnected */}
            <div className="bg-zinc-900 border border-red-500/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <WifiOff className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">WhatsApp Desconectado</h3>
                  <p className="text-sm text-zinc-400">{attention.whatsapp_disconnected?.length || 0} clientes</p>
                </div>
              </div>
              <div className="space-y-2">
                {attention.whatsapp_disconnected?.slice(0, 5).map((item) => (
                  <Link
                    key={item.id}
                    to={`/admin/clients/${item.id}`}
                    className="block p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    <p className="text-sm font-medium text-white">{item.user?.name || "Sem nome"}</p>
                    <p className="text-xs text-zinc-400">{item.user?.email}</p>
                  </Link>
                ))}
                {attention.whatsapp_disconnected?.length > 5 && (
                  <Link
                    to="/admin/support"
                    className="block text-center text-sm text-emerald-500 hover:text-emerald-400 mt-3"
                  >
                    Ver todos ({attention.whatsapp_disconnected.length})
                  </Link>
                )}
              </div>
            </div>

            {/* Integration Errors */}
            <div className="bg-zinc-900 border border-yellow-500/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Erros de Integração</h3>
                  <p className="text-sm text-zinc-400">{attention.integration_errors?.length || 0} clientes</p>
                </div>
              </div>
              <div className="space-y-2">
                {attention.integration_errors?.slice(0, 5).map((item) => (
                  <Link
                    key={item.id}
                    to={`/admin/clients/${item.id}`}
                    className="block p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    <p className="text-sm font-medium text-white">{item.user?.name || "Sem nome"}</p>
                    <p className="text-xs text-zinc-400">{item.user?.email}</p>
                  </Link>
                ))}
                {attention.integration_errors?.length > 5 && (
                  <Link
                    to="/admin/support"
                    className="block text-center text-sm text-emerald-500 hover:text-emerald-400 mt-3"
                  >
                    Ver todos ({attention.integration_errors.length})
                  </Link>
                )}
              </div>
            </div>

            {/* Unconfigured */}
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-zinc-700 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-zinc-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Sem Configuração</h3>
                  <p className="text-sm text-zinc-400">{attention.unconfigured?.length || 0} clientes</p>
                </div>
              </div>
              <div className="space-y-2">
                {attention.unconfigured?.slice(0, 5).map((item) => (
                  <Link
                    key={item.id}
                    to={`/admin/clients/${item.id}`}
                    className="block p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    <p className="text-sm font-medium text-white">{item.user?.name || "Sem nome"}</p>
                    <p className="text-xs text-zinc-400">{item.user?.email}</p>
                  </Link>
                ))}
                {attention.unconfigured?.length > 5 && (
                  <Link
                    to="/admin/support"
                    className="block text-center text-sm text-emerald-500 hover:text-emerald-400 mt-3"
                  >
                    Ver todos ({attention.unconfigured.length})
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
